import * as go from 'gojs';
import {
  getDefaultPictureSize,
  getImageHeightBinding,
  getImageWidthBinding,
  mirroredImageBinding,
} from './image';
import {
  bodyPanel,
  locationBinding,
  roundedRectangleShape,
  rowColumnDefinition,
} from './node';
import { getSelectionAdornment } from './selectionAdornment';
import type { Options } from './types';
import { defaultImageSize, defaultMargins, defaultNodeSize } from './types';
import { BaseUnit, parallelUnits } from '../Panels/baseUnitPanel';
import { attachmentToolPanel } from './attachmentUnitPanel';
import { attachmentToolBindings } from './attachmentTool';
import type { GyratoryCrusherNodeData, JawCrusherNodeData } from 'gojs/types';
import {
  handleFreePortArrowSelectionChanged,
  hideFreePortHoverArrows,
  showFreePortHoverArrows,
} from './freePortHoverArrows';

const $ = go.GraphObject.make;

export const mobileUnitDefaultOptions = {
  nodeSize: defaultNodeSize.wide,
  imageSize: defaultImageSize.wide,
  imageMargin: 8,
};

/**
 * @param imageBinding go.Binding with a callback function for changing the image
 * @param panels List of panels to render. Should include e.g input/output ports
 * @param options Additional options
 * @returns go.Node
 */
export const makeNode = (
  imageBinding: () => go.Binding,
  panels: go.Panel[],
  options?: Options,
) =>
  $(
    go.Node,
    {
      layerName: 'Foreground',
      name: 'NODEPARENT',
      mouseEnter: (_e, obj) => showFreePortHoverArrows(obj.part as go.Node),
      mouseLeave: (_e, obj) => hideFreePortHoverArrows(obj.part as go.Node),
      selectionChanged: handleFreePortArrowSelectionChanged,
    },
    'Table',
    ...rowColumnDefinition(),
    getSelectionAdornment(),
    locationBinding(),
    new go.Binding('isSelected', 'selected'),
    $(
      go.Panel,
      'Spot',
      { isPanelMain: true },
      bodyPanel(),
      roundedRectangleShape(options?.nodeSize ?? defaultNodeSize.square),
      parallelUnits(imageBinding, mirroredImageBinding, options),
      $(
        go.Picture,
        getDefaultPictureSize(
          options?.imageSize ?? defaultImageSize.square,
          options?.imageMargin ?? defaultMargins,
        ),
        getImageWidthBinding(
          options?.imageSize?.width ?? defaultImageSize.square.width,
        ),
        getImageHeightBinding(
          options?.imageSize?.height ?? defaultImageSize.square.height,
        ),
        mirroredImageBinding(),
        imageBinding(),
        attachmentToolBindings.map((b) => b()),
        new go.Binding(
          'scale',
          '',
          (data: JawCrusherNodeData | GyratoryCrusherNodeData) =>
            data?.attachmentTool?.enabled ? 0.8 : 1,
        ),
      ),
      ...panels,
      attachmentToolPanel(),
      BaseUnit(),
    ),
  );
